<div class="app-main__inner">

    <div class="app-page-title">
        <div class="page-title-wrapper">
            <div class="page-title-heading">
                <div class="page-title-icon">
                    <i class="pe-7s-speaker icon-gradient bg-tempting-azure"></i>
                </div>
                <div>
                    Campaign Creation
                    <div class="page-title-subheading">
                        Define campaigns by choosing a template and setting variable values. Trigger them from <strong>Trigger Campaign</strong>.
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Toolbar -->
    <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
            <span *ngIf="resultMessage" class="alert py-2 px-3 mb-0"
                [ngClass]="resultSuccess ? 'alert-success' : 'alert-danger'">
                <i [ngClass]="resultSuccess ? 'pe-7s-check' : 'pe-7s-attention'" class="me-1"></i>
                {{ resultMessage }}
            </span>
        </div>
        <button class="btn btn-primary" (click)="showCreateForm = !showCreateForm">
            <i class="pe-7s-plus me-1"></i>
            {{ showCreateForm ? 'Cancel' : 'Create Campaign' }}
        </button>
    </div>

    <!-- Create Campaign Form -->
    <div *ngIf="showCreateForm" class="mb-4">
        <div class="row g-3 align-items-start">

            <!-- Left: form steps -->
            <div class="col-lg-7">

                <!-- Step 1: Name + Template -->
                <div class="card mb-3">
                    <div class="card-header d-flex align-items-center gap-2">
                        <span class="step-badge">1</span>
                        <span class="fw-semibold">Campaign Details</span>
                    </div>
                    <div class="card-body">
                        <div class="mb-3">
                            <label class="form-label fw-semibold">Campaign Name</label>
                            <input type="text" class="form-control form-control-lg"
                                [(ngModel)]="campaignName"
                                (ngModelChange)="onCampaignNameChange()"
                                placeholder="e.g. Summer Sale 2026"
                                maxlength="100" />
                        </div>
                        <div>
                            <label class="form-label fw-semibold">Template</label>
                            <div *ngIf="isLoadingApprovedTemplates" class="text-muted small py-2">
                                <span class="spinner-border spinner-border-sm me-1"></span> Loading approved templates...
                            </div>
                            <div *ngIf="!isLoadingApprovedTemplates && approvedTemplates.length === 0"
                                class="alert alert-warning py-2 px-3 mb-0">
                                No approved templates yet. Go to <strong>Template Creation</strong> first.
                            </div>
                            <select *ngIf="!isLoadingApprovedTemplates && approvedTemplates.length > 0"
                                class="form-select form-select-lg"
                                [ngModel]="selectedTemplate?._id"
                                (ngModelChange)="onTemplateSelect($event)">
                                <option value="">— Select a template —</option>
                                <option *ngFor="let t of approvedTemplates" [value]="t._id">
                                    {{ t.templateName }} ({{ templateFormatLabel(t) }})
                                </option>
                            </select>
                            <div *ngIf="selectedTemplate" class="mt-2 d-flex gap-2 flex-wrap">
                                <span class="badge bg-success-subtle text-success border border-success-subtle">APPROVED</span>
                                <span class="badge bg-secondary-subtle text-secondary border border-secondary-subtle">{{ selectedTemplate.language }}</span>
                                <span class="badge bg-primary-subtle text-primary border border-primary-subtle">{{ templateFormatLabel(selectedTemplate) }}</span>
                                <span class="badge bg-info-subtle text-info border border-info-subtle">{{ selectedTemplate.templateType }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Step 2: Variable Values -->
                <div *ngIf="selectedTemplate && (hasOuterVar() || selectedTemplate.cardBodyTexts?.length)" class="card mb-3">
                    <div class="card-header d-flex align-items-center gap-2">
                        <span class="step-badge">2</span>
                        <span class="fw-semibold">Message Variables</span>
                        <span class="text-muted fw-normal small">— values that replace <code>[variable N]</code> placeholders in the template</span>
                    </div>
                    <div class="card-body">
                        <div *ngIf="hasOuterVar()" class="mb-4">
                            <label class="form-label fw-semibold">
                                {{ tplFormat === 'carousel' ? 'Outer Body Variables' : 'Body Variables' }}
                            </label>
                            <div class="text-muted small font-monospace mb-2">"{{ selectedTemplate.outerBodyText }}"</div>
                            <div *ngFor="let vc of outerVarConfigs; let vi = index; trackBy: trackByIndex"
                                class="border rounded p-2 mb-2">
                                <div class="small fw-semibold mb-2">
                                    <span class="var-pill">[variable {{ vi + 1 }}]</span>
                                    <span class="text-muted ms-2" style="font-weight:400;">Provide the value that will replace this placeholder in the message</span>
                                </div>
                                <div class="d-flex gap-3 mb-2">
                                    <div class="form-check">
                                        <input class="form-check-input" type="radio"
                                            [(ngModel)]="outerVarConfigs[vi].type"
                                            value="hardcoded"
                                            [name]="'outerVarType' + vi"
                                            [id]="'outerHardcoded' + vi" />
                                        <label class="form-check-label" [for]="'outerHardcoded' + vi">Fixed text</label>
                                    </div>
                                    <div class="form-check">
                                        <input class="form-check-input" type="radio"
                                            [(ngModel)]="outerVarConfigs[vi].type"
                                            value="userField"
                                            [name]="'outerVarType' + vi"
                                            [id]="'outerUserField' + vi" />
                                        <label class="form-check-label" [for]="'outerUserField' + vi">From User Master</label>
                                    </div>
                                </div>
                                <input *ngIf="outerVarConfigs[vi].type === 'hardcoded'" type="text"
                                    class="form-control" [(ngModel)]="outerVarConfigs[vi].value"
                                    maxlength="100" placeholder="Hardcoded text sent to every recipient" />
                                <select *ngIf="outerVarConfigs[vi].type === 'userField'"
                                    class="form-select" [(ngModel)]="outerVarConfigs[vi].fieldName">
                                    <option value="">— Select user master field —</option>
                                    <option *ngFor="let f of userMasterFields" [value]="f.fieldName">{{ f.label }}</option>
                                </select>
                            </div>
                        </div>
                        <div *ngIf="selectedTemplate.cardBodyTexts?.length">
                            <label class="form-label fw-semibold">Card Variables</label>
                            <div class="row g-2">
                                <div *ngFor="let cardCfgs of cardVarConfigs; let i = index; trackBy: trackByIndex" class="col-md-6">
                                    <div class="border rounded p-2">
                                        <div class="small fw-semibold text-muted mb-1">Card {{ i + 1 }}</div>
                                        <div *ngIf="selectedTemplate.cardBodyTexts[i]" class="text-muted small font-monospace mb-1">
                                            "{{ selectedTemplate.cardBodyTexts[i] }}"
                                        </div>
                                        <ng-container *ngIf="hasCardVar(i)">
                                            <div *ngFor="let vcfg of cardVarConfigs[i]; let vi = index; trackBy: trackByIndex"
                                                class="border-top pt-2 mt-1" [class.border-top]="vi > 0">
                                                <div class="small fw-semibold mb-1">
                                                    <span class="var-pill">[variable {{ vi + 1 }}]</span>
                                                    <span class="text-muted ms-1" style="font-weight:400;">Replace this placeholder</span>
                                                </div>
                                                <div class="d-flex gap-2 mb-1">
                                                    <div class="form-check form-check-inline">
                                                        <input class="form-check-input" type="radio"
                                                            [(ngModel)]="cardVarConfigs[i][vi].type"
                                                            value="hardcoded"
                                                            [name]="'cardVarType' + i + '_' + vi"
                                                            [id]="'cardHardcoded' + i + '_' + vi" />
                                                        <label class="form-check-label small" [for]="'cardHardcoded' + i + '_' + vi">Fixed</label>
                                                    </div>
                                                    <div class="form-check form-check-inline">
                                                        <input class="form-check-input" type="radio"
                                                            [(ngModel)]="cardVarConfigs[i][vi].type"
                                                            value="userField"
                                                            [name]="'cardVarType' + i + '_' + vi"
                                                            [id]="'cardUserField' + i + '_' + vi" />
                                                        <label class="form-check-label small" [for]="'cardUserField' + i + '_' + vi">User Master</label>
                                                    </div>
                                                </div>
                                                <input *ngIf="cardVarConfigs[i][vi].type === 'hardcoded'" type="text"
                                                    class="form-control form-control-sm"
                                                    [(ngModel)]="cardVarConfigs[i][vi].value" maxlength="160"
                                                    [placeholder]="'Fixed text for card ' + (i+1)" />
                                                <select *ngIf="cardVarConfigs[i][vi].type === 'userField'"
                                                    class="form-select form-select-sm"
                                                    [(ngModel)]="cardVarConfigs[i][vi].fieldName">
                                                    <option value="">— Select field —</option>
                                                    <option *ngFor="let f of userMasterFields" [value]="f.fieldName">{{ f.label }}</option>
                                                </select>
                                            </div>
                                        </ng-container>
                                        <div *ngIf="!hasCardVar(i)" class="text-muted small fst-italic">No variable in this card.</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Save bar -->
                <div class="d-flex justify-content-between align-items-center px-1">
                    <div *ngIf="validationHint && !isSaving" class="validation-hint">
                        <i class="pe-7s-info me-1"></i> {{ validationHint }}
                    </div>
                    <div class="ms-auto d-flex gap-2">
                        <button
                            *ngIf="selectedTemplate"
                            class="btn btn-outline-success btn-lg"
                            (click)="openTestMessageModal()"
                            title="Send a test message to a number before saving"
                        >
                            <i class="pe-7s-phone me-2"></i>Send Test Message
                        </button>
                        <button class="btn btn-primary btn-lg px-5" (click)="save()" [disabled]="!isValid || isSaving">
                            <span *ngIf="isSaving"><span class="spinner-border spinner-border-sm me-2"></span>Saving...</span>
                            <span *ngIf="!isSaving"><i class="pe-7s-diskette me-2"></i>Save Campaign</span>
                        </button>
                    </div>
                </div>

            </div>

            <!-- Right: WhatsApp preview -->
            <div class="col-lg-5">
                <div class="card tpl-preview-card">
                    <div class="card-header d-flex align-items-center gap-2">
                        <i class="pe-7s-phone me-1"></i>
                        <span class="fw-semibold">Template Preview</span>
                        <span *ngIf="selectedTemplate" class="badge bg-success-subtle text-success border border-success-subtle ms-auto">
                            {{ selectedTemplate.templateName }}
                        </span>
                    </div>
                    <div class="card-body d-flex align-items-start justify-content-center py-4">

                        <!-- Placeholder when no template selected -->
                        <div *ngIf="!selectedTemplate" class="text-center text-muted py-4">
                            <i class="pe-7s-phone" style="font-size:2.5rem; opacity:0.25;"></i>
                            <div class="mt-2 small">Select a template to see the preview</div>
                        </div>

                        <!-- WhatsApp mock -->
                        <div *ngIf="selectedTemplate" class="wa-mock">
                            <div class="wa-mock-header">
                                <div class="wa-mock-avatar"></div>
                                <div class="wa-mock-name">Business</div>
                            </div>
                            <div class="wa-mock-body">

                                <!-- TEXT format -->
                                <ng-container *ngIf="tplFormat === 'text'">
                                    <div class="wa-msg-bubble">
                                        <div class="wa-msg-text">{{ previewBodyText(selectedTemplate.outerBodyText, outerVarConfigs) }}</div>
                                        <div class="wa-msg-btns" *ngIf="selectedTemplate.buttonNames?.length">
                                            <div *ngFor="let b of selectedTemplate.buttonNames" class="wa-btn">{{ b }}</div>
                                        </div>
                                        <span class="wa-msg-time">10:30 AM</span>
                                    </div>
                                </ng-container>

                                <!-- IMAGE format -->
                                <ng-container *ngIf="tplFormat === 'image'">
                                    <div class="wa-msg-bubble wa-msg-bubble--img">
                                        <div class="wa-img-header">
                                            <img *ngIf="selectedTemplate.cardImageUrls?.[0]"
                                                [src]="selectedTemplate.cardImageUrls![0]"
                                                class="wa-header-img" alt="Template image" />
                                            <div *ngIf="!selectedTemplate.cardImageUrls?.[0]" class="wa-img-placeholder">
                                                <i class="pe-7s-photo"></i>
                                                <div class="small">Image</div>
                                            </div>
                                        </div>
                                        <div class="wa-msg-text">{{ previewBodyText(selectedTemplate.outerBodyText, outerVarConfigs) }}</div>
                                        <div class="wa-msg-btns" *ngIf="selectedTemplate.buttonNames?.length">
                                            <div *ngFor="let b of selectedTemplate.buttonNames" class="wa-btn">{{ b }}</div>
                                        </div>
                                        <span class="wa-msg-time">10:30 AM</span>
                                    </div>
                                </ng-container>

                                <!-- CAROUSEL format -->
                                <ng-container *ngIf="tplFormat === 'carousel'">
                                    <div class="wa-msg-bubble">
                                        <div class="wa-msg-text">{{ previewBodyText(selectedTemplate.outerBodyText, outerVarConfigs) }}</div>
                                        <span class="wa-msg-time">10:30 AM</span>
                                    </div>
                                    <div class="wa-carousel-strip">
                                        <div *ngFor="let cardText of selectedTemplate.cardBodyTexts; let i = index"
                                            class="wa-carousel-card">
                                            <div class="wa-card-img-wrap">
                                                <img *ngIf="selectedTemplate.cardImageUrls?.[i]"
                                                    [src]="selectedTemplate.cardImageUrls![i]"
                                                    class="wa-card-img" alt="Card image" />
                                                <div *ngIf="!selectedTemplate.cardImageUrls?.[i]"
                                                    class="wa-card-img-placeholder">
                                                    <i class="pe-7s-photo"></i>
                                                    <div>Card {{ i + 1 }}</div>
                                                </div>
                                            </div>
                                            <div class="wa-card-body-text">{{ previewBodyText(cardText, cardVarConfigs[i] ?? []) }}</div>
                                            <div class="wa-card-btns" *ngIf="selectedTemplate.buttonNames?.length">
                                                <div *ngFor="let b of selectedTemplate.buttonNames" class="wa-btn">{{ b }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </ng-container>

                            </div>
                        </div>

                    </div>
                </div>
            </div>

        </div>
    </div>

    <!-- Saved Campaigns List -->
    <div class="card">
        <div class="card-header d-flex align-items-center gap-2">
            <i class="header-icon pe-7s-speaker icon-gradient bg-tempting-azure"></i>
            <span class="fw-semibold">Saved Campaigns</span>
            <span class="badge bg-secondary rounded-pill ms-1">{{ savedCampaigns.length }}</span>
        </div>
        <div class="card-body p-0">
            <div *ngIf="isLoadingCampaigns" class="text-center p-4 text-muted">
                <span class="spinner-border spinner-border-sm me-2"></span> Loading...
            </div>
            <div *ngIf="!isLoadingCampaigns && savedCampaigns.length === 0" class="text-center p-4 text-muted">
                No campaigns yet. Click <strong>Create Campaign</strong> to define your first one.
            </div>
            <vio-table-v2
                *ngIf="!isLoadingCampaigns && savedCampaigns.length > 0"
                [tableData]="savedCampaignsForTable"
                [tableConfig]="savedCampaignsTableConfig">
            </vio-table-v2>
        </div>
    </div>

</div>

<!-- ── Test Message Modal ───────────────────────────────────────────────────── -->
<div class="modal fade" tabindex="-1" aria-hidden="true" #testMessageModal>
    <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
            <div class="modal-header">
                <div class="d-flex align-items-center gap-2">
                    <i class="pe-7s-phone icon-gradient bg-happy-fisher" style="font-size:1.4rem;"></i>
                    <h5 class="modal-title mb-0">Send Test Message</h5>
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
                <p class="text-muted small mb-3">
                    Send this campaign's message to a single WhatsApp number to preview how it looks.
                    Variable values are applied as configured — <em>User Master</em> fields will be resolved
                    from the recipient's record if they exist in the system.
                </p>
                <div *ngIf="selectedTemplate" class="alert alert-light border py-2 small mb-3">
                    <span class="fw-semibold">Template:</span> {{ selectedTemplate.templateName }}
                    &nbsp;·&nbsp;
                    <span class="fw-semibold">Format:</span> {{ templateFormatLabel(selectedTemplate) }}
                </div>
                <div class="mb-3">
                    <label class="form-label fw-semibold">
                        WhatsApp Number <span class="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        class="form-control"
                        [(ngModel)]="testPhoneNo"
                        placeholder="e.g. 919876543210 (with country code, no +)"
                        [disabled]="isSendingTest"
                    />
                    <div class="form-text">Include country code without the + sign.</div>
                </div>
                <div *ngIf="testMsgResult" class="alert py-2 small"
                    [ngClass]="testMsgSuccess ? 'alert-success' : 'alert-danger'">
                    <i [ngClass]="testMsgSuccess ? 'pe-7s-check' : 'pe-7s-attention'" class="me-1"></i>
                    {{ testMsgResult }}
                </div>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" [disabled]="isSendingTest">Cancel</button>
                <button
                    type="button"
                    class="btn btn-success"
                    (click)="sendTestMessage()"
                    [disabled]="!testPhoneNo.trim() || isSendingTest"
                >
                    <span *ngIf="isSendingTest" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    {{ isSendingTest ? 'Sending…' : 'Send Test' }}
                </button>
            </div>
        </div>
    </div>
</div>

<!-- ── Campaign Runs Modal ──────────────────────────────────────────────────── -->
<div class="modal fade" id="campaignRunsModal" tabindex="-1" aria-hidden="true" #runsModal>
    <div class="modal-dialog modal-xl">
        <div class="modal-content">
            <div class="modal-header">
                <div class="d-flex align-items-center gap-2">
                    <i class="pe-7s-timer icon-gradient bg-tempting-azure" style="font-size:1.4rem;"></i>
                    <h5 class="modal-title mb-0">Campaign Runs</h5>
                    <span *ngIf="selectedCampaignForRuns" class="text-muted fw-normal" style="font-size:0.85rem;">
                        — {{ selectedCampaignForRuns.campaignName }}
                    </span>
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body p-0" style="max-height:70vh;overflow-y:auto;">
                <div *ngIf="isLoadingCampaignRuns" class="text-center py-5 text-muted">
                    <span class="spinner-border spinner-border-sm me-2"></span> Loading runs...
                </div>
                <div *ngIf="!isLoadingCampaignRuns && campaignRunsForModal.length === 0" class="text-center py-5 text-muted">
                    No runs found for this campaign yet.
                </div>
                <vio-table-v2
                    *ngIf="!isLoadingCampaignRuns && campaignRunsForModal.length > 0"
                    [tableData]="campaignRunsForModal"
                    [tableConfig]="campaignRunsTableConfig">
                </vio-table-v2>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
        </div>
    </div>
</div>

<!-- ── Recipients Modal ─────────────────────────────────────────────────────── -->
<div class="modal fade" id="campaignRecipientsModal" tabindex="-1" aria-hidden="true" #recipientsModal>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <div class="d-flex align-items-center gap-2">
                    <i class="pe-7s-users icon-gradient bg-happy-fisher" style="font-size:1.4rem;"></i>
                    <h5 class="modal-title mb-0">Recipients</h5>
                    <span class="text-muted fw-normal" style="font-size:0.85rem;">— {{ recipientsLabel }}</span>
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body p-0" style="max-height:60vh;overflow-y:auto;">
                <div *ngIf="recipientsData.length === 0" class="text-center py-5 text-muted">No recipients found.</div>
                <table *ngIf="recipientsData.length > 0" class="table table-sm table-hover align-middle mb-0">
                    <thead class="table-light">
                        <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>WhatsApp No.</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let r of recipientsData; let i = index">
                            <td class="text-muted">{{ i + 1 }}</td>
                            <td>{{ r.name || (r.firstName ? r.firstName + ' ' + (r.lastName || '') : '—') }}</td>
                            <td><code>{{ r.whatsAppNo }}</code></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="modal-footer">
                <span class="text-muted small me-auto">{{ recipientsData.length }} recipient(s)</span>
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
        </div>
    </div>
</div>

<!-- ── Message Status Modal ─────────────────────────────────────────────────── -->
<div class="modal fade" id="campaignDeliveryModal" tabindex="-1" aria-hidden="true" #deliveryModal>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <div class="d-flex align-items-center gap-2">
                    <i class="pe-7s-graph1 icon-gradient bg-tempting-azure" style="font-size:1.4rem;"></i>
                    <h5 class="modal-title mb-0">Message Status</h5>
                    <span class="text-muted fw-normal" style="font-size:0.85rem;">— {{ deliveryRunLabel }}</span>
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body" style="max-height:70vh;overflow-y:auto;">
                <div *ngIf="isLoadingDelivery" class="text-center py-5 text-muted">
                    <span class="spinner-border spinner-border-sm me-2"></span> Loading...
                </div>
                <div *ngIf="!isLoadingDelivery && !deliveryData" class="text-center py-5 text-muted">
                    No delivery data found.
                </div>
                <ng-container *ngIf="!isLoadingDelivery && deliveryData">
                    <div class="d-flex gap-3 flex-wrap mb-4">
                        <div class="delivery-stat"><div class="delivery-stat__count text-secondary">{{ deliveryData.summary.total }}</div><div class="delivery-stat__label">Total</div></div>
                        <div class="delivery-stat"><div class="delivery-stat__count text-primary">{{ deliveryData.summary.sent }}</div><div class="delivery-stat__label">Sent</div></div>
                        <div class="delivery-stat"><div class="delivery-stat__count text-success">{{ deliveryData.summary.delivered }}</div><div class="delivery-stat__label">Delivered</div></div>
                        <div class="delivery-stat"><div class="delivery-stat__count text-info">{{ deliveryData.summary.read }}</div><div class="delivery-stat__label">Read</div></div>
                        <div class="delivery-stat"><div class="delivery-stat__count text-danger">{{ deliveryData.summary.failed }}</div><div class="delivery-stat__label">Failed</div></div>
                    </div>
                    <div *ngIf="deliveryData.logs.length === 0" class="text-center text-muted py-4">No log entries yet.</div>
                    <div class="table-responsive" *ngIf="deliveryData.logs.length > 0">
                        <table class="table table-sm table-hover align-middle mb-0">
                            <thead class="table-light">
                                <tr><th>Name</th><th>WhatsApp No.</th><th>Status</th><th>Delivered At</th><th>Read At</th></tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let log of deliveryData.logs">
                                    <td>{{ log.recipientName }}</td>
                                    <td>{{ log.recipientNo }}</td>
                                    <td>
                                        <span class="badge"
                                            [ngClass]="{
                                                'bg-secondary': log.status===20,
                                                'bg-primary':   log.status===30,
                                                'bg-success':   log.status===40,
                                                'bg-danger':    log.status===10
                                            }">{{ statusLabel(log.status) }}</span>
                                        <ng-container *ngIf="log.status===10">
                                            <div class="text-danger small mt-1" style="max-width:260px;line-height:1.3;">
                                                <span *ngIf="log.failedErrorCode" class="fw-semibold">Code {{ log.failedErrorCode }}: </span>{{ log.failedReason || 'Delivery failed' }}
                                            </div>
                                        </ng-container>
                                    </td>
                                    <td>{{ log.deliveredAt ? (log.deliveredAt | date:'dd MMM h:mm a') : '—' }}</td>
                                    <td>{{ log.readAt ? (log.readAt | date:'dd MMM h:mm a') : '—' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ng-container>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
        </div>
    </div>
</div>
